import { useQuery } from '@tanstack/react-query'
import backend from '@api/backend'
import {
  IRenewalSummaryCard,
  IRenewalRegionRow,
  ICustomerRenewalRow,
  ICustomerRenewalDetail,
  IStripeEventRow,
} from './model/renewals.model'

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const useRenewalSummary = () => {
  return useQuery({
    queryKey: ['renewal-summary'],
    queryFn: getRenewalSummary,
    staleTime: 1000 * 60 * 5,
  })
}

export const useRenewalsRegions = () => {
  return useQuery({
    queryKey: ['renewals-regions'],
    queryFn: getRenewalsRegions,
    staleTime: 1000 * 60 * 5,
  })
}

export const useRenewalsList = (daysWindow: number = 30) => {
  return useQuery({
    queryKey: ['renewals-list', daysWindow],
    queryFn: () => getRenewalsList(daysWindow),
    staleTime: 1000 * 60 * 5,
  })
}

export const useCustomerRenewalDetail = (accountId: number | undefined) => {
  return useQuery({
    queryKey: ['customer-renewal-detail', accountId],
    queryFn: () => getCustomerRenewalDetail(accountId!),
    enabled: !!accountId,
    // Always re-fetch on mount so navigating to a detail page reflects the
    // latest Stripe webhook state without needing a manual refresh.
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

export const useStripeEventTimeline = (accountId: number | undefined, enabled = true) => {
  return useQuery({
    queryKey: ['stripe-timeline', accountId],
    queryFn: () => getStripeEventTimeline(accountId!),
    enabled: !!accountId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  })
}

// ---------------------------------------------------------------------------
// Async functions
// ---------------------------------------------------------------------------

async function getRenewalSummary(): Promise<IRenewalSummaryCard[]> {
  const response = await backend.get('/renewals/summary')
  return response.data as IRenewalSummaryCard[]
}

async function getRenewalsRegions(): Promise<IRenewalRegionRow[]> {
  const response = await backend.get('/renewals/regions')
  return response.data as IRenewalRegionRow[]
}

async function getRenewalsList(daysWindow: number): Promise<ICustomerRenewalRow[]> {
  const response = await backend.get('/renewals/list', {
    params: { days_window: daysWindow },
  })
  return response.data as ICustomerRenewalRow[]
}

async function getCustomerRenewalDetail(accountId: number): Promise<ICustomerRenewalDetail> {
  const response = await backend.get(`/renewals/customer/${accountId}`)
  return response.data as ICustomerRenewalDetail
}

async function getStripeEventTimeline(accountId: number): Promise<IStripeEventRow[]> {
  const response = await backend.get(`/renewals/customer/${accountId}/timeline`)
  return response.data as IStripeEventRow[]
}
